import React, { useState } from 'react';
import { TabProps, Tabs, TabsList, TabsTrigger } from '../../../../../../../../../components/Common/UI/Tabs';
import { Button } from '../../../../../../../../../components/Common/UI/Button';
import Icons from '../../../../../../../../../components/Common/Icons/Icons';
import { Switch } from '../../../../../../../../../components/Common/UI/Switch';
import Label from '../../../../../../../../../components/Common/UI/Label';
import { useMessageManagerProvider } from '../../../../../../../../context/messageManager';
import ModuleEditBlock from '../Block';
import { cn } from '../../../../../../../../../utils/styles';
import ModalModuleMedia from '../../../Modals/Media';
import Checkbox from '../../../../../../../../../components/Common/UI/Checkbox';
import { Input } from '../../../../../../../../../components/Common/UI/Input';

enum EnumStatement {
  eStatement = 'eStatement',
  paperStatement = 'paperStatement',
}

function MediaBlock({ tab }: { tab: number }) {
  const [isChecked, setIsChecked] = useState<boolean>(false);

  const { selectedModule, handleIsTextOnly, handleDispatch, handleSelectedModule, state } = useMessageManagerProvider();

  const { endOfStatement } = state.step2;
  const selected = tab === 0 ? selectedModule?.item : endOfStatement.selected;
  const emptyMessageId = `empty-message-${tab}`;
  const mirrorPrintId = `mirrorPrint-${tab}`;
  const trackableId = `trackable-${tab}`;

  const tabs: TabProps[] = [
    {
      value: EnumStatement.eStatement,
      label: 'eStatement',
      content: <img src={selected?.imageUrl} alt="selected" />,
    },
    {
      value: EnumStatement.paperStatement,
      label: 'Paper Statement',
      content: <img src={selected?.imageUrl} alt="selected" />,
    },
  ];

  const handleTabChange = (payload: string) => {
    if (selected && tab === 0) {
      handleSelectedModule({
        ...selectedModule,
        item: {
          ...selected,
          eStatement: payload === EnumStatement.eStatement,
        },
      });
    }

    if (selected && tab === 1) {
      handleDispatch({
        step2: {
          ...state.step2,
          endOfStatement: {
            ...endOfStatement,
            selected: { ...selected, eStatement: payload === EnumStatement.eStatement },
          },
        },
      });
    }
  };

  return (
    <ModuleEditBlock
      title="Media"
      subTitle={
        tab === 0 ? (
          'You can upload content to display in your module or chose text only option'
        ) : (
          <div className="mt-[10px]">
            <div className="font-bold text-addition1 uppercase">Please Note</div>
            <div className="mb-[10px]">
              To ensure your end statement graphic is printed in the highest quality possible, please follow the below
              guidelines when using an image that is larger then 1/3 of the page:
            </div>
            <ul className="!list-disc !pl-[30px]">
              <li>Avoid solid color blocks covering more then 1/3 of the page vertically.</li>
              <li>Avoid darker images covering more then 1/3 of the page vertically.</li>
              <li>Avoid dark text on dark backgrounds.</li>
              <li>
                Ensure all images are uploaded with at least 7.5 inches with width, and no more than 10.4 inches in
                height.
              </li>
            </ul>
          </div>
        )
      }
    >
      <div className="flex gap-[20px] items-start">
        <Tabs
          defaultValue="eStatement"
          value={selected?.eStatement === true ? EnumStatement.eStatement : EnumStatement.paperStatement}
          onValueChange={val => handleTabChange(val)}
        >
          <TabsList className="h-[50px] rounded-b-none">
            {tabs.map(({ value, label }) => (
              <TabsTrigger
                key={`${value}-trigger`}
                value={value}
                className="h-[44px] min-w-[160px] rounded-lg font-bold"
              >
                {label}
              </TabsTrigger>
            ))}
          </TabsList>
          <div
            className="flex w-[328px] rounded-b-lg overflow-hidden bg-lightGray2 border h-[275px] items-center
          justify-center"
          >
            {selected?.imageUrl ? (
              <img
                alt="selected"
                src={selected?.qrCode && !selected?.eStatement ? '/infoTrac/images/step-2-QR.png' : selected?.imageUrl}
                className={cn('h-full object-contain', {
                  grayscale: !selected?.eStatement && !selected?.qrCode,
                })}
              />
            ) : (
              <Icons.Image className="opacity-20 mx-auto" />
            )}
          </div>
        </Tabs>
        <div className="grid grid-rows-[repeat(3, auto)] gap-y-[10px] justify-items-start">
          {tab === 0 && state.step2.statement.mode === 'text' ? (
            <Button
              className="bg-primary text-white"
              onClick={() => {
                handleIsTextOnly(true);
                if (selectedModule) handleSelectedModule({ ...selectedModule, textTabIndex: 0 });
                handleDispatch({
                  step2: { ...state.step2, statement: { ...state.step2.statement, mode: 'text' } },
                });
              }}
            >
              <Icons.TextEditFilled className="child:fill-white" />
              Edit text only
            </Button>
          ) : null}
          <ModalModuleMedia
            triggerStyle="p-0 self-start"
            trigger={
              <div
                className="bg-primary text-white flex gap-[5px] px-[20px] py-[9px] rounded-lg items-center
                 hover:bg-[#009DA8]"
              >
                <Icons.Library className="child:fill-white" />
                Choose from media library
              </div>
            }
          />
          <div className="grid gap-[15px] justify-items-start">
            {selected?.eStatement ? (
              <>
                <div className="flex gap-2 items-center justify-center">
                  <Checkbox id={emptyMessageId} />
                  <label
                    htmlFor={emptyMessageId}
                    className="text-sm text-primary cursor-pointer font-medium peer-disabled:cursor-not-allowed
                  peer-disabled:opacity-70"
                  >
                    Empty message content
                  </label>
                </div>
                <div className="flex items-center gap-x-[5px]">
                  <Switch id={mirrorPrintId} checked={isChecked} onCheckedChange={e => setIsChecked(e)} />
                  <Label htmlFor={mirrorPrintId} className="cursor-pointer !m-0 text-[16px] text-primary font-normal">
                    Mirror Print & Image
                  </Label>
                </div>
                <Input
                  labelClassName="mb-[10px]"
                  label="Click through URL:"
                  placeholder="trackable url"
                  className="w-[350px]"
                  variant="white"
                />
                <div className="flex gap-2 items-center justify-center">
                  <Checkbox id={trackableId} defaultChecked />
                  <label
                    htmlFor={trackableId}
                    className="text-sm text-primary cursor-pointer font-medium peer-disabled:cursor-not-allowed
                  peer-disabled:opacity-70"
                  >
                    Trackable
                  </label>
                </div>
              </>
            ) : (
              <>
                <div className="flex gap-2 items-center justify-center">
                  <Checkbox id={emptyMessageId} />
                  <label
                    htmlFor={emptyMessageId}
                    className="text-sm text-primary cursor-pointer font-medium peer-disabled:cursor-not-allowed
                  peer-disabled:opacity-70"
                  >
                    Empty message content
                  </label>
                </div>
                <div className="flex items-center gap-x-[5px]">
                  <Switch id={mirrorPrintId} checked={isChecked} onCheckedChange={e => setIsChecked(e)} />
                  <Label htmlFor={mirrorPrintId} className="cursor-pointer !m-0 text-[16px] text-primary font-normal">
                    {selected?.eStatement ? 'Mirror Print & Image' : 'Mirror from eStatement to paper'}
                  </Label>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </ModuleEditBlock>
  );
}

export default MediaBlock;
